import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { BellRing, Mail, Phone, Check, RefreshCw, Plus } from "lucide-react";
import { toast } from "sonner";
import LocationAutocomplete from "./LocationAutocomplete";

const CreateAlertModal = ({ isOpen, onClose, onCreateAlert }) => {
  const [alertName, setAlertName] = useState("");
  const [filters, setFilters] = useState({
    city: null, state: null, zip_code: null,
    min_price: null, max_price: null,
    min_beds: null, max_beds: null,
    property_types: ["single_family", "multi_family", "townhouse"],
    only_profitable: true,
  });
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!alertName.trim()) { toast.error("Please enter an alert name"); return; }
    if (notifyEmail && !recipientEmail.trim()) { toast.error("Please enter an email address for notifications"); return; }
    // SMS validation skipped — notifySms toggle is hidden until SMS is re-enabled.
    setLoading(true);
    try {
      await onCreateAlert({
        name: alertName,
        filters: { ...filters, property_types: filters.property_types.length > 0 ? filters.property_types : null },
        notify_email: notifyEmail, notify_sms: notifySms,
        phone_number: notifySms ? phoneNumber : null,
        recipient_email: notifyEmail ? recipientEmail : null,
      });
      onClose();
      setAlertName("");
    } catch (error) {
      console.error("Error creating alert:", error);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-border" data-testid="create-alert-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="w-5 h-5 text-emerald-500" /> Create Property Alert
          </DialogTitle>
          <DialogDescription>Get notified when new properties match your criteria</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Alert Name</Label>
            <Input value={alertName} onChange={(e) => setAlertName(e.target.value)} placeholder="e.g., Houston Multi-Family" className="mt-1" data-testid="alert-name-input" />
          </div>
          <Separator />
          <div>
            <Label className="text-xs text-muted-foreground">Location (City, State, or Zip)</Label>
            <LocationAutocomplete
              value={filters.city || ""}
              onChange={(val) => setFilters({...filters, city: val || null})}
              onSelect={(display) => setFilters({...filters, city: display || null})}
              placeholder="e.g., Miami, FL or 33165"
              className="mt-1"
              data-testid="alert-location-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Min Price</Label>
              <Input type="number" value={filters.min_price || ""} onChange={(e) => setFilters({...filters, min_price: e.target.value ? parseInt(e.target.value) : null})} placeholder="Any" className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Max Price</Label>
              <Input type="number" value={filters.max_price || ""} onChange={(e) => setFilters({...filters, max_price: e.target.value ? parseInt(e.target.value) : null})} placeholder="Any" className="mt-1 font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Min Bedrooms</Label>
              <Input type="number" value={filters.min_beds || ""} onChange={(e) => setFilters({...filters, min_beds: e.target.value ? parseInt(e.target.value) : null})} placeholder="Any" className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Max Bedrooms</Label>
              <Input type="number" value={filters.max_beds || ""} onChange={(e) => setFilters({...filters, max_beds: e.target.value ? parseInt(e.target.value) : null})} placeholder="Any" className="mt-1 font-mono" />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="only-profitable" checked={filters.only_profitable} onCheckedChange={(checked) => setFilters({...filters, only_profitable: checked})} />
            <label htmlFor="only-profitable" className="text-sm">Only profitable properties (passes 1% rule)</label>
          </div>
          <Separator />
          <div className="space-y-3">
            <Label>Notification Methods</Label>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" /><span className="text-sm">Email Notifications</span></div>
              <Switch checked={notifyEmail} onCheckedChange={setNotifyEmail} />
            </div>
            {notifyEmail && <Input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="your@email.com" className="mt-1" type="email" data-testid="alert-email-input" />}
            {/* SMS notification toggle hidden — feature paused. State kept
                so existing alerts with notify_sms=true still round-trip
                cleanly when re-enabled later. */}
            <p className="text-xs text-emerald-500 flex items-center gap-1"><Check className="w-3 h-3" /> Email alerts powered by Resend</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={loading} data-testid="create-alert-btn">
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Alert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAlertModal;
